import * as THREE from "three";

extendMeshBasicMaterialWithEmissiveIntensity();

const materialList = [];

export const materials = {
    list: materialList,
    setMaterial: setMaterial,
    updatableMaterials: [],
    getMaterial: getMaterial,
    useBoxProjectedEnvMap,
};

function getMaterial(name) {
    return materialList.find((mat) => mat.name === name);
}

export function setMaterial(object) {
    let existingMaterial;
    if (materialList.length > 0) {
        existingMaterial = materialList.find((mat) => mat.name === object.material.name);
    }

    if (existingMaterial) {
        object.material = existingMaterial.isUnique ? existingMaterial.clone() : existingMaterial;
        return;
    }

    object.material.aoMapIntensity = 0.8;

    if (object.name.includes("shkaf")) object.material.aoMapIntensity = 1.25;

    materialList.push(object.material);
}

function extendMeshBasicMaterialWithEmissiveIntensity() {
    Object.defineProperty(THREE.MeshBasicMaterial.prototype, "emissiveIntensity", {
        get: function () {
            return this._emissiveIntensity !== undefined ? this._emissiveIntensity : 1.0;
        },
        set: function (value) {
            this._emissiveIntensity = value;
            if (this.userData && this.userData.shader) {
                this.userData.shader.uniforms.emissiveIntensity.value = value;
            }
        },
    });

    THREE.MeshBasicMaterial.prototype.onBeforeCompile = function (shader) {
        shader.uniforms.emissiveIntensity = { value: this.emissiveIntensity };
        shader.fragmentShader = "uniform float emissiveIntensity;\n" + shader.fragmentShader;
        shader.fragmentShader = shader.fragmentShader.replace(
            "vec4 diffuseColor = vec4( diffuse, opacity );",
            "vec4 diffuseColor = vec4( diffuse * emissiveIntensity, opacity );",
        );
        this.userData.shader = shader;
    };
}

export function useBoxProjectedEnvMap(shader, envMapPosition, envMapSize) {
    const worldposReplace = /* glsl */ `
    #if defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP )
        vec4 worldPosition = modelMatrix * vec4( transformed, 1.0 );
        #ifdef BOX_PROJECTED_ENV_MAP
            vWorldPosition = worldPosition.xyz;
        #endif
    #endif
    `;

    const boxProjectDefinitions = /*glsl */ `
    #ifdef BOX_PROJECTED_ENV_MAP
        uniform vec3 envMapSize;
        uniform vec3 envMapPosition;
        varying vec3 vWorldPosition;
        
        vec3 parallaxCorrectNormal( vec3 v, vec3 cubeSize, vec3 cubePos ) {
            vec3 nDir = normalize( v );
            vec3 rbmax = ( .5 * cubeSize + cubePos - vWorldPosition ) / nDir;
            vec3 rbmin = ( -.5 * cubeSize + cubePos - vWorldPosition ) / nDir;
            vec3 rbminmax;
            rbminmax.x = ( nDir.x > 0. ) ? rbmax.x : rbmin.x;
            rbminmax.y = ( nDir.y > 0. ) ? rbmax.y : rbmin.y;
            rbminmax.z = ( nDir.z > 0. ) ? rbmax.z : rbmin.z;
            float correction = min( min( rbminmax.x, rbminmax.y ), rbminmax.z );
            vec3 boxIntersection = vWorldPosition + nDir * correction;
            
            return boxIntersection - cubePos;
        }
    #endif
    `;

    // will be inserted after "vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );"
    const getIBLIrradiance_patch = /* glsl */ `
    #ifdef BOX_PROJECTED_ENV_MAP
        worldNormal = parallaxCorrectNormal( worldNormal, envMapSize, envMapPosition );
    #endif
    `;

    // will be inserted after "reflectVec = inverseTransformDirection( reflectVec, viewMatrix );"
    const getIBLRadiance_patch = /* glsl */ `
    #ifdef BOX_PROJECTED_ENV_MAP
        reflectVec = parallaxCorrectNormal( reflectVec, envMapSize, envMapPosition );
    #endif
    `;

    if (!shader.uniforms) shader.uniforms = {};

    // defines
    shader.defines.BOX_PROJECTED_ENV_MAP = true;

    // uniforms
    shader.uniforms.envMapPosition = {
        value: envMapPosition,
    };

    shader.uniforms.envMapSize = {
        value: envMapSize,
    };

    // vertex shader
    shader.vertexShader =
        "varying vec3 vWorldPosition;\n" + shader.vertexShader.replace("#include <worldpos_vertex>", worldposReplace);

    // fragment shader
    shader.fragmentShader =
        boxProjectDefinitions +
        "\n" +
        shader.fragmentShader
            .replace("#include <envmap_physical_pars_fragment>", THREE.ShaderChunk.envmap_physical_pars_fragment)
            .replace(
                "vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );",
                `
            vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );
            ${getIBLIrradiance_patch}
            `,
            )
            .replace(
                "reflectVec = inverseTransformDirection( reflectVec, viewMatrix );",
                `
            reflectVec = inverseTransformDirection( reflectVec, viewMatrix );
            ${getIBLRadiance_patch}
            `,
            );
}
